import { Link } from 'react-router-dom';
import { formatMoney } from '@hfb/shared';
import { useCart } from '../context/CartContext.jsx';
import { usePlans, useProducts } from '../hooks/useCatalog.js';
import { cadenceLabel, unitPrice } from '../lib/pricing.js';

/**
 * Floating "view cart" bar, shown app-wide whenever the one-off cart has items.
 * Adding to the cart never needs an account — this is how a shopper gets from
 * anywhere to the (login-gated) checkout to place the order.
 */
function CartBarInner() {
  const { items, frequency } = useCart();
  const { data: products } = useProducts();
  const { data: plans = [] } = usePlans();
  const byId = new Map((products ?? []).map((p) => [p.id, p]));
  const subtotal = items.reduce((s, it) => s + unitPrice(byId.get(it.id), frequency.repeat) * it.quantity, 0);
  const count = items.reduce((s, it) => s + it.quantity, 0);
  const plan = plans.find((p) => (p.slug ?? p.id) === frequency.planSlug);

  return (
    <div className="pad-safe-x pointer-events-none fixed inset-x-0 bottom-20 z-40 px-4 sm:bottom-6">
      <Link
        to="/cart"
        className="pointer-events-auto mx-auto flex max-w-md items-center gap-3 rounded-full bg-leaf-800 py-2 pl-2 pr-2 text-milk shadow-lift transition-transform active:scale-[0.99] sm:hover:brightness-110"
      >
        {/* Basket with a count badge. */}
        <span className="relative grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15" aria-label={count === 1 ? '1 item' : `${count} items`}>
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9h16l-1 11H5L4 9Zm4 0V7a4 4 0 0 1 8 0v2" />
          </svg>
          <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-persimmon px-1 text-2xs font-bold tabular-nums text-white">
            {count}
          </span>
        </span>

        {/* Total on top, how it's bought underneath — one line each, never wrapping. */}
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-base font-bold tabular-nums">{formatMoney(subtotal)}</span>
          <span className="block truncate text-2xs font-medium text-milk/75">
            {frequency.repeat ? `🔁 Repeats · ${cadenceLabel(plan)}` : 'One-time order'}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-milk px-4 py-2.5 text-sm font-bold text-leaf-800">
          View basket
          <span aria-hidden="true">›</span>
        </span>
      </Link>
    </div>
  );
}

// The count check is split out so the products query only runs when there's a
// cart to price — an empty cart renders (and fetches) nothing.
export default function CartBar() {
  const { count } = useCart();
  if (count === 0) return null;
  return <CartBarInner />;
}
